// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Pop bumper (PH-A20).
//
// The kick **sets** the outgoing radial speed rather than adding to it. That is not a
// shortcut — it is what the mechanism does. A pop bumper is a ring driven down onto the
// ball by a solenoid with a fixed stroke and a fixed energy, so the ball leaves at the
// bumper's speed and not at its own plus the bumper's. Adding instead of setting is how
// bumper nests turn into particle accelerators, with the ball gaining energy on every
// bounce until it is pinned in the corner of the glass.
//
// A ball already leaving faster than the kick is left alone, since the ring cannot catch it.

import type { Ball } from '../Ball'
import type { Collider } from '../colliders'
import { BALL_RADIUS } from '../constants'
import { makeCircle } from '../colliders'
import type { MaterialName } from '../materials'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_BUMPER_HIT, EV_SWITCH_CLOSED } from '../events'

export interface PopBumperConfig {
  readonly id: string
  readonly centre: readonly [number, number]
  /** Radius of the skirt the ball touches. */
  readonly skirtRadius: number
  /** Speed the ball leaves at, m/s. Real bumpers land in 3.0–4.0. */
  readonly kickSpeed?: number
  /** Steps before the coil can fire again. A real bumper needs 60–100 ms to reset. */
  readonly cooldownSteps?: number
  /** Normal approach speed below which the ball merely touches the skirt, m/s. */
  readonly triggerSpeed?: number
  readonly material?: MaterialName
}

export class PopBumper extends Device {
  readonly kind = 'popBumper'

  readonly cx: number
  readonly cy: number
  readonly skirtRadius: number

  private readonly kickSpeed: number
  private readonly cooldownSteps: number
  private readonly triggerSpeed: number
  private readonly material: MaterialName
  private readyAtStep = 0

  /** Kicks delivered since the last reset. */
  hits = 0

  /**
   * Step index of the most recent kick, or −1 if the coil has not fired since the last reset.
   *
   * A coil fire is instantaneous to the simulation and takes about forty milliseconds in the
   * world — the ring slams down, the arm punches out, and both come back. A renderer needs to
   * know *when*, and `hits` only says *how many*: recovering the moment from a counter means
   * the renderer keeping its own memory of the last value it saw, which is wrong after a
   * dropped frame and wrong again after a reset, and wrong in the quietest possible way — the
   * mechanism simply stops moving while the game plays on perfectly. One integer, written
   * where the counter is already written, removes the whole problem.
   */
  firedAtStep = -1

  protected override ownState(): DeviceState {
    return { readyAtStep: this.readyAtStep, hits: this.hits, firedAtStep: this.firedAtStep }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.readyAtStep = Number(state['readyAtStep'])
    this.hits = Number(state['hits'])
    this.firedAtStep = Number(state['firedAtStep'])
  }

  constructor(config: PopBumperConfig) {
    super(config.id)
    this.cx = config.centre[0]
    this.cy = config.centre[1]
    this.skirtRadius = config.skirtRadius
    this.setOrigin(this.cx, this.cy)
    this.kickSpeed = config.kickSpeed ?? 3.4
    this.cooldownSteps = config.cooldownSteps ?? 80
    this.triggerSpeed = config.triggerSpeed ?? 0.25
    this.material = config.material ?? 'rubber'
  }

  override colliders(): readonly Collider[] {
    return [makeCircle(this.cx, this.cy, this.skirtRadius, this.material, { device: this.index })]
  }

  override onColliderHit(
    world: World,
    ball: Ball,
    nx: number,
    ny: number,
    impactSpeed: number,
  ): void {
    if (impactSpeed < this.triggerSpeed) return
    if (world.stepIndex < this.readyAtStep) return
    this.readyAtStep = world.stepIndex + this.cooldownSteps
    this.firedAtStep = world.stepIndex
    this.hits++

    const outward = ball.vx * nx + ball.vy * ny
    if (outward < this.kickSpeed) {
      const delta = this.kickSpeed - outward
      ball.vx += nx * delta
      ball.vy += ny * delta
    }

    world.events.emit(EV_BUMPER_HIT, this.index, ball.id, world.stepIndex, this.kickSpeed)
    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, impactSpeed)
  }

  /**
   * Fire the coil directly, without a ball having hit anything.
   *
   * This is what ball search stage 1 means by "pulse the coils near the ball", and without it
   * the stage is theatre: a ball resting on a bumper skirt below the trigger speed produces no
   * impact, so `onColliderHit` never runs, so the bumper never fires and the search escalates
   * past two perfectly good stages to a teleport. Returns whether it moved anything.
   */
  fire(world: World): boolean {
    // Stamped before anything is looked for. The coil fires either way: a real machine's ball
    // search is *audible and visible* precisely because every bumper it pulses pops whether or
    // not there was a ball on it, and that is the feedback telling the player the machine has
    // noticed. Stamping only on success would make a search that finds nothing look like a
    // machine that has frozen.
    this.firedAtStep = world.stepIndex
    let moved = false
    const reach = this.skirtRadius + BALL_RADIUS + 0.002
    for (const ball of world.balls) {
      if (!ball.active || ball.heldBy >= 0) continue
      const dx = ball.x - this.cx
      const dy = ball.y - this.cy
      const d2 = dx * dx + dy * dy
      if (d2 > reach * reach || d2 === 0) continue
      const d = Math.sqrt(d2)
      const nx = dx / d
      const ny = dy / d
      const outward = ball.vx * nx + ball.vy * ny
      if (outward < this.kickSpeed) {
        ball.vx += nx * (this.kickSpeed - outward)
        ball.vy += ny * (this.kickSpeed - outward)
      }
      this.hits++
      moved = true
      world.events.emit(EV_BUMPER_HIT, this.index, ball.id, world.stepIndex, this.kickSpeed)
      world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, this.kickSpeed)
    }
    return moved
  }

  override reset(): void {
    this.readyAtStep = 0
    this.firedAtStep = -1
    this.hits = 0
  }
}
